import { inject, Injectable } from '@angular/core';

import { BrowserProvider, Contract, formatEther, formatUnits, getAddress, JsonRpcSigner } from 'ethers';

import { SUPPORTED_EVM_CHAINS } from '@drf/core';

import { Eip1193Provider, WalletConnectionError, WalletConnectionErrorCode, WalletConnector, WalletProviderConnector } from '../models';
import { WALLET_CONNECTOR } from '../tokens';
import { normalizeWalletTokenDecimals } from '../utils';

const ERC20_BALANCE_ABI = ['function balanceOf(address owner) view returns (uint256)', 'function decimals() view returns (uint8)'] as const;
const NATIVE_ASSET_DECIMALS = 18;

export interface WalletBalanceResult {
  balance: string;
  decimals: number;
  walletAddress: string;
}

export interface ConnectedWalletIdentityResult {
  chainId: number;
  walletAddress: string;
}

@Injectable({ providedIn: 'root' })
export class EvmWalletAccessService {
  readonly #connector = inject(WALLET_CONNECTOR);
  readonly #supportedChains = inject(SUPPORTED_EVM_CHAINS);
  #cachedBrowserProvider: BrowserProvider | null = null;
  #cachedEip1193Provider: Eip1193Provider | null = null;

  async getSigner(requiredChainId: number): Promise<JsonRpcSigner> {
    this.#assertSupportedRequiredChain(requiredChainId);
    const connector = this.#requireProviderConnector();
    const identity = await connector.getConnectedIdentity();

    if (!identity) {
      throw new WalletConnectionError(WalletConnectionErrorCode.NoAccounts, 'No connected wallet identity is available.');
    }

    if (identity.chainId !== requiredChainId) {
      throw new WalletConnectionError(
        WalletConnectionErrorCode.UnsupportedChain,
        `Connected wallet is on unsupported EVM chain: ${identity.chainId}`,
      );
    }

    // Reuse one BrowserProvider per EIP-1193 instance. Creating a new one on every call
    // attaches more MetaMask EventEmitter listeners and can break tx.wait() polling.
    const browserProvider = await this.#getBrowserProvider(connector);
    const network = await browserProvider.getNetwork();

    if (Number(network.chainId) !== requiredChainId) {
      throw new WalletConnectionError(
        WalletConnectionErrorCode.UnsupportedChain,
        `Wallet provider is on unsupported EVM chain: ${network.chainId}`,
      );
    }

    const signer = await browserProvider.getSigner();
    const signerAddress = getAddress(await signer.getAddress());

    if (signerAddress !== identity.accountAddress) {
      throw new WalletConnectionError(WalletConnectionErrorCode.Unauthorized, 'Wallet signer does not match the connected identity.');
    }

    return signer;
  }

  // Clear cache after reject/RPC failures; skip destroy() so in-flight waits stay alive.
  resetBrowserProvider(): void {
    this.#cachedBrowserProvider = null;
    this.#cachedEip1193Provider = null;
  }

  async getErc20Balance(requiredChainId: number, tokenAddress: string): Promise<WalletBalanceResult> {
    const signer = await this.getSigner(requiredChainId);
    const walletAddress = getAddress(await signer.getAddress());
    const tokenContract = new Contract(tokenAddress, ERC20_BALANCE_ABI, signer);
    const [rawBalance, decimals] = (await Promise.all([tokenContract['balanceOf'](walletAddress), tokenContract['decimals']()])) as [
      bigint,
      bigint,
    ];
    const tokenDecimals = normalizeWalletTokenDecimals(decimals);
    const balance = formatUnits(rawBalance, tokenDecimals);

    return {
      balance,
      decimals: tokenDecimals,
      walletAddress,
    };
  }

  async getConnectedWalletAddress(): Promise<string> {
    const identity = await this.getConnectedWalletIdentity();

    return identity.walletAddress;
  }

  async getConnectedWalletIdentity(): Promise<ConnectedWalletIdentityResult> {
    const connector = this.#requireProviderConnector();
    const identity = await connector.getConnectedIdentity();

    if (!identity) {
      throw new WalletConnectionError(WalletConnectionErrorCode.NoAccounts, 'No connected wallet identity is available.');
    }

    return {
      chainId: identity.chainId,
      walletAddress: identity.accountAddress,
    };
  }

  async getNativeBalance(requiredChainId: number): Promise<WalletBalanceResult> {
    const signer = await this.getSigner(requiredChainId);
    const walletAddress = getAddress(await signer.getAddress());
    const rawBalance = await signer.provider.getBalance(walletAddress);
    const balance = formatEther(rawBalance);

    return {
      balance,
      decimals: NATIVE_ASSET_DECIMALS,
      walletAddress,
    };
  }

  #assertSupportedRequiredChain(requiredChainId: number): void {
    if (!Number.isSafeInteger(requiredChainId) || requiredChainId < 1 || !isSupportedChain(requiredChainId, this.#supportedChains)) {
      throw new WalletConnectionError(WalletConnectionErrorCode.UnsupportedChain, `Unsupported EVM chain: ${requiredChainId}`);
    }
  }

  async #getBrowserProvider(connector: WalletProviderConnector): Promise<BrowserProvider> {
    const eip1193Provider = await connector.getEip1193Provider();

    if (this.#cachedEip1193Provider === eip1193Provider && this.#cachedBrowserProvider) {
      return this.#cachedBrowserProvider;
    }

    const previousProvider = this.#cachedBrowserProvider;
    this.#cachedEip1193Provider = eip1193Provider;
    // "any" re-detects network/block tip instead of pinning a static network snapshot.
    const browserProvider = new BrowserProvider(eip1193Provider, 'any');
    this.#cachedBrowserProvider = browserProvider;
    void previousProvider?.destroy();

    return browserProvider;
  }

  #requireProviderConnector(): WalletProviderConnector {
    if (!isWalletProviderConnector(this.#connector)) {
      throw new WalletConnectionError(
        WalletConnectionErrorCode.UnsupportedMethod,
        'The active wallet connector does not expose signer access.',
      );
    }

    return this.#connector;
  }
}

function isWalletProviderConnector(connector: WalletConnector): connector is WalletProviderConnector {
  return 'getEip1193Provider' in connector && typeof connector.getEip1193Provider === 'function';
}

function isSupportedChain(chainId: number, supportedChains: readonly { chainId: number }[]): boolean {
  return supportedChains.length === 0 || supportedChains.some(chain => chain.chainId === chainId);
}
